import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { Box, IconButton, Input } from '@mui/material';
import { styled } from '@mui/system';
import React, { FC, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import WsColors from '../../WsColors';

export const enum SearchBarVariant {
  WITH_SEARCH_BTN = 'with-search-btn',
  WITHOUT_SEARCH_BTN = 'without-search-btn',
}

export interface SearchBarNewProps {
  name: string;
  placeholder?: string;
  size?: 'small' | 'medium' | 'large';
  type?: 'button' | 'submit';
  variant?: SearchBarVariant;
  onSearch?: (event: React.MouseEvent) => void;
  onClear?: (event: React.MouseEvent) => void;
}

const SearchWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderRadius: 6,
  border: `1px solid ${WsColors.GreyBlack}`,
  overflow: 'hidden',
});

const StyledIconButton = styled(IconButton)(({ hidden }) => ({
  width: 52,
  backgroundColor: WsColors.TheBlues500,
  borderRadius: 0,
  color: WsColors.White,
  '&:hover': {
    backgroundColor: WsColors.TheBlues700,
  },
  display: hidden ? 'none' : 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));

enum SearchBarHeight {
  small = '32px',
  medium = '40px',
  large = '48px',
}

const SearchBarNew: FC<SearchBarNewProps> = ({
  name,
  placeholder = 'Search',
  type = 'button',
  size = 'medium',
  variant = SearchBarVariant.WITH_SEARCH_BTN,
  onSearch,
  onClear,
}) => {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [focused, setFocused] = useState<boolean>(false);
  const { register, setValue, getValues } = useFormContext();
  const showOptionalIcon = focused || getValues(name)?.length > 0;

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.stopPropagation();
      buttonRef.current?.click();
    }
  };

  return (
    <SearchWrapper
      sx={{
        border: focused ? `2px solid ${WsColors.TheBlues500}` : `1px solid ${WsColors.GreyBlack}`,
        height: SearchBarHeight[size],
        '&:hover': {
          backgroundColor: focused ? WsColors.White : WsColors.Gray88,
        },
      }}
    >
      {variant == SearchBarVariant.WITHOUT_SEARCH_BTN && (
        <Box ml={1} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <SearchIcon />
        </Box>
      )}
      <Box flexGrow={1} px={1} onKeyUp={handleKeyPress}>
        <Input
          {...register(name)}
          placeholder={placeholder}
          onBlur={() => setFocused(false)}
          onFocus={() => setFocused(true)}
          fullWidth
          disableUnderline
        />
      </Box>
      {showOptionalIcon && (
        <IconButton
          size="small"
          sx={{
            position: 'relative',
            right: 1,
            color: 'black',
          }}
          onClick={(event) => {
            setValue(name, '');
            onClear ? onClear(event) : buttonRef.current?.click();
          }}
        >
          <CloseIcon />
        </IconButton>
      )}
      <StyledIconButton
        hidden={variant == SearchBarVariant.WITHOUT_SEARCH_BTN}
        ref={buttonRef}
        type={type}
        onClick={onSearch}
      >
        <SearchIcon />
      </StyledIconButton>
    </SearchWrapper>
  );
};

export default SearchBarNew;
